import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Calendar, User, Mail, Phone, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TypingIndicator = ({ name }) => (
  <div className="flex justify-start">
    <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
      <div className="flex gap-1.5 items-center">
        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-[bounce_0.6s_infinite_0ms]"></span>
        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-[bounce_0.6s_infinite_150ms]"></span>
        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-[bounce_0.6s_infinite_300ms]"></span>
        <span className="text-xs text-gray-400 ml-2">{name || 'Typing'} is typing</span>
      </div>
    </div>
  </div>
);

const InlineCaptureForm = ({ onSubmit, onSkip }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const handleSubmit = (e) => {
    e.preventDefault();
    if (form.name || form.email || form.phone) onSubmit(form);
  };
  return (
    <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200 mx-2 my-2">
      <p className="text-sm font-semibold text-emerald-800 mb-3">Quick way to connect — fill this and we'll get back to you:</p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
          <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
            className="flex-1 text-sm outline-none" data-testid="chat-form-name" />
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
          <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input placeholder="Phone number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
            className="flex-1 text-sm outline-none" data-testid="chat-form-phone" />
        </div>
        <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input placeholder="Email (optional)" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
            className="flex-1 text-sm outline-none" data-testid="chat-form-email" />
        </div>
        <div className="flex gap-2">
          <button type="submit" data-testid="chat-form-submit"
            className="flex-1 bg-emerald-600 text-white text-sm font-medium py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1">
            <ArrowRight className="w-3 h-3" /> Connect Me
          </button>
          <button type="button" onClick={onSkip}
            className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Skip
          </button>
        </div>
      </form>
    </div>
  );
};

const ChatWidget = ({ onBookAppointment }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [personaName, setPersonaName] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages, sending, showForm]);
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  // Show unread badge after 8s
  useEffect(() => {
    const timer = setTimeout(() => { if (!hasGreeted && !isOpen) setUnread(1); }, 8000);
    return () => clearTimeout(timer);
  }, [hasGreeted, isOpen]);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setUnread(0);
    if (!hasGreeted) {
      setHasGreeted(true);
      // Fetch persona name from backend on first open
      fetch(`${BACKEND_URL}/api/chat/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: 'hi' })
      }).then(r => r.json()).then(data => {
        const name = data.persona_name || 'Sowmya';
        setPersonaName(name);
        if (data.session_id) setSessionId(data.session_id);
        setMessages([{ role: 'assistant', text: data.reply }]);
      }).catch(() => {
        setPersonaName('Sowmya');
        setMessages([{
          role: 'assistant',
          text: "Hey! I'm Sowmya from Virtual CFO Consultancy. Whether it's GST troubles, accounting chaos, or setting up in India — I can help. What's going on?",
        }]);
      });
    }
  }, [hasGreeted]);

  const addAssistantMsg = (text) => {
    setMessages(prev => [...prev, { role: 'assistant', text }]);
  };

  const handleFormSubmit = async (form) => {
    setShowForm(false);
    setFormSubmitted(true);
    const parts = [];
    if (form.name) parts.push(form.name);
    if (form.phone) parts.push(form.phone);
    if (form.email) parts.push(form.email);

    // Show as user message
    setMessages(prev => [...prev, { role: 'user', text: `${form.name || ''} | ${form.phone || ''} | ${form.email || ''}`.replace(/^ \| | \| $/g, '') }]);

    // Send to backend for lead capture
    try {
      await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: `My name is ${form.name}, phone: ${form.phone}, email: ${form.email}`, session_id: sessionId })
      });
    } catch (e) { console.error('Chat contact submission failed:', e); }

    addAssistantMsg(`Thanks ${form.name || ''}! Our team will reach out shortly. Meanwhile, would you like to book a specific time slot? You can book a free consultation right now.`);
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setSending(true);
    const newCount = msgCount + 1;
    setMsgCount(newCount);

    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg, session_id: sessionId })
      });

      if (res.ok) {
        const data = await res.json();
        if (!sessionId) setSessionId(data.session_id);
        if (data.persona_name) setPersonaName(data.persona_name);
        setSending(false);
        addAssistantMsg(data.reply);

        // Show form after 2nd exchange if not already submitted
        if (newCount >= 2 && !formSubmitted && !data.lead_captured) {
          setShowForm(true);
        }
      } else {
        setSending(false);
        addAssistantMsg("Sorry, brief hiccup on my end. Try again or book a free consultation — our team will call you!");
      }
    } catch (e) {
      setSending(false);
      addAssistantMsg("Connection issue — you can also book a free consultation directly and we'll call you within 2 hours!");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const renderText = (text) => {
    const parts = text.split(/(book a free consultation|book a consultation|book a meeting|schedule a call)/gi);
    return parts.map((part, i) => {
      if (/book a (free )?consultation|book a meeting|schedule a call/i.test(part)) {
        return (
          <button key={`cta-${i}`} onClick={() => { if (onBookAppointment) onBookAppointment(); }}
            className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-medium hover:bg-emerald-700 transition-colors mx-0.5 my-0.5">
            <Calendar className="w-3 h-3" /> {part}
          </button>
        );
      }
      return <span key={`part-${i}`}>{part}</span>;
    });
  };

  return (
    <>
      {/* Bubble */}
      {!isOpen && (
        <button onClick={openChat} data-testid="chat-widget-trigger"
          className="fixed bottom-6 right-4 sm:right-6 z-[60] w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-all duration-300 group">
          <MessageCircle className="w-7 h-7 text-white" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center animate-bounce">{unread}</span>
          )}
          <span className="absolute right-20 bg-gray-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Chat with us
          </span>
        </button>
      )}

      {/* Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-4 sm:right-6 z-[60] w-[380px] max-w-[calc(100vw-2rem)] h-[540px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="chat-widget-panel">

          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white font-bold text-lg">{(personaName || 'S')[0]}</div>
              <div>
                <p className="text-white font-semibold text-sm" data-testid="chat-persona-name">{personaName || 'NSRM'}</p>
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></span>
                  <span className="text-emerald-100 text-xs">Online now</span>
                </div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={msg.timestamp || `msg-${i}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-emerald-600 text-white rounded-br-sm'
                    : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                }`} data-testid={`chat-msg-${i}`}>
                  {msg.role === 'assistant' ? renderText(msg.text) : msg.text}
                </div>
              </div>
            ))}

            {sending && <TypingIndicator name={personaName} />}

            {showForm && !formSubmitted && (
              <InlineCaptureForm onSubmit={handleFormSubmit} onSkip={() => setShowForm(false)} />
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions - only first exchange */}
          {messages.length <= 1 && !sending && (
            <div className="px-3 py-2 bg-white border-t border-gray-100 flex gap-2 overflow-x-auto flex-shrink-0">
              {[
                "Help with GST/TDS",
                "Your pricing plans",
                "Set up company in India"
              ].map((q, i) => (
                <button key={`suggestion-${i}`} onClick={() => setInput(q)}
                  className="whitespace-nowrap text-xs bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full border border-emerald-200 hover:bg-emerald-100 transition-colors flex-shrink-0">
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-3 bg-white border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder="Type your message..." data-testid="chat-input"
                className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all" />
              <button onClick={sendMessage} disabled={!input.trim() || sending} data-testid="chat-send-btn"
                className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
